import type { ReactNode } from 'react';
import { Button } from './Button';
import { Icon } from './Icon';
import './PageHeader.scss';

interface PageHeaderProps {
  title: ReactNode;
  subtitle?: ReactNode;
  /** 'view' shows Edit, 'edit' shows Cancel and Save, none shows only `children`. */
  mode?: 'view' | 'edit';
  onEdit?: () => void;
  onSave?: () => void;
  onCancel?: () => void;
  /** Disables Edit (e.g. while the record loads). */
  canEdit?: boolean;
  /** Disables Save (e.g. nothing changed or the form is invalid). */
  canSave?: boolean;
  /** Saving: both buttons are disabled so it cannot be sent twice. */
  busy?: boolean;
  editLabel?: string;
  /**
   * "Always editable" pages (2 Nov design review): there is no Edit step. Pass whether the form differs from what was
   * saved; while it does, the header shows an "Unsaved changes" hint with Discard and Save, otherwise only `children`.
   * Used instead of `mode`.
   */
  dirty?: boolean;
  onDiscard?: () => void;
  /** Extra actions shown before the buttons, e.g. a "Reset to default" link or an info hint. */
  children?: ReactNode;
}

/**
 * Design review 2 Nov 2026: one page header, title on the left and the page's actions on the right, which stays at the top
 * of the scrolling area so Edit/Save are always in view - replacing the fixed footer (`EditSaveActions`). Put it as the
 * first child of the scrolling area (`.form`), or sticking has nothing to stick to.
 */
export function PageHeader({
  title,
  subtitle,
  mode,
  onEdit,
  onSave,
  onCancel,
  canEdit = true,
  canSave = true,
  busy = false,
  editLabel = 'Edit',
  dirty,
  onDiscard,
  children,
}: PageHeaderProps) {
  return (
    <header className="kit-page-header">
      <div className="kit-page-header__text">
        <h2 className="kit-page-header__title">{title}</h2>
        {subtitle && <p className="kit-page-header__subtitle">{subtitle}</p>}
      </div>
      <div className="kit-page-header__actions">
        {children}
        {dirty && (
          <>
            <span className="kit-page-header__dirty" role="status">
              Unsaved changes
            </span>
            <Button className="action-button secondary-button" disabled={busy} onClick={onDiscard}>
              Discard
            </Button>
            <Button className="action-button primary-button" disabled={busy || !canSave} onClick={onSave}>
              <Icon name="save" outlined />
              <span>Save</span>
            </Button>
          </>
        )}
        {mode === 'edit' && (
          <>
            <Button className="action-button secondary-button" disabled={busy} onClick={onCancel}>
              Cancel
            </Button>
            <Button className="action-button primary-button" disabled={busy || !canSave} onClick={onSave}>
              <Icon name="save" outlined />
              <span>Save</span>
            </Button>
          </>
        )}
        {mode === 'view' && (
          <Button className="action-button primary-button btn-edit" disabled={!canEdit} onClick={onEdit}>
            <Icon name="edit" outlined />
            <span>{editLabel}</span>
          </Button>
        )}
      </div>
    </header>
  );
}
